// What the views draw: the live game, or the step the history cursor is on.
// A view that shows the board or anything read from it uses these, not the
// game store.

import { otherSide } from '../engine/pieces';
import { type Cue, cueFor, holdCue, reviewCue } from '../game/cue';
import { canRoll, type GameState } from '../game/state';
import { gameStore } from './game';
import { historyStore, reviewedStep } from './history';
import { localSide, localSideStore, spectatingStore } from './localSide';
import { isMatchWaiting, matchWaitStore } from './matchWait';
import { settingsStore } from './settings';
import { stageStore } from './stage';

export const viewedGame = (): GameState => reviewedStep()?.state ?? gameStore.get();

export const currentCue = (): Cue => {
  const { steps, cursor } = historyStore.get();
  const step = cursor === null ? null : steps[cursor];
  if (cursor === null || !step) {
    if (isMatchWaiting()) return holdCue();
    const live = gameStore.get();
    // A spectator acts for no side, so every turn is the turn of the other side.
    const side = spectatingStore.get() ? otherSide(live.turn) : localSide();
    return cueFor(live, side);
  }
  return reviewCue(step.line, cursor, steps.length - 1);
};

/**
 * A roll may start or is in progress, for either side. The compact layout shows the wheel
 * only then. The row goes when the card leaves the board.
 */
export const isRollMoment = (): boolean => {
  if (historyStore.get().cursor !== null) return false;
  const stage = stageStore.get().phase;
  return canRoll(gameStore.get()) || stage === 'spinning' || stage === 'landed';
};

/**
 * Calls the listener when the game, the cursor, a setting, or the side of
 * this screen changes. The cue reads one setting and the side.
 */
export const subscribeView = (listener: () => void): void => {
  gameStore.subscribe(listener);
  historyStore.subscribe(listener);
  settingsStore.subscribe(listener);
  localSideStore.subscribe(listener);
  spectatingStore.subscribe(listener);
  matchWaitStore.subscribe(listener);
};
